import { Container } from "@shared-components/Container/Container";
import { Heading } from "@shared-components/Heading/Heading";
import { LayoutContainer } from "@shared-components/LayoutContainer/LayoutContainer";
import { Box, Text as ReactText } from "theme-ui";
import React, { Fragment, useEffect, useState } from "react";
import {
  headingStylesCollection,
  textStylesCollection,
  textStylesMatch,
} from "../../missionHybridSlider/MultiSliderModule/styles";

import { flavourHeadingStyles } from "../styles";
import { Transition, Variants, motion } from "framer-motion";
import transitions from "@styles/design-tokens/09.transitions/transitions";
import { useInView } from "react-intersection-observer";
import { useWindowSize } from "../../../shared/hooks/useWindowSize/useWindowSize";
import { Button } from "@shared-components/Button/Button";
import { generateCbKey } from "../../../shared/utils/generate-cbKey";
import { Icon } from "@shared-components/Icon/Icon";
import { Image } from "@shared-components/Image/Image";
import MediaSliderArrow from "@svg-icons/media-slider-arrow.component.svg";
import MediaSliderEllipse from "@svg-icons/media-slider-ellipse.component.svg";
import {
  ellipseStyles,
  leftArrowSpanStyles,
  leftArrowStyles,
  rightArrowSpanStyles,
  rightArrowStyles,
  swiperPaginationStyles,
  swiperStyles,
} from "./styles";
import { THEMES_MAP } from "../../../carlsberg-headless/themes-map";
import type { Flavour } from "../../../carlsberg-headless/types/modules/digital-memories";
import { useDictionaryContext } from "../../../context/dictionary/useDictionaryContext";
import { useSiteContext } from "../../../context/site/useSiteContext";
import { DICTIONARY_MAP } from "../../../carlsberg-headless/dictionary-map";

export type FlavourModuleProps = {
  themes: string[];
  bottleIds: [];
};
export type FlavourSliderProps = {
  adv: number;
  advText: string;
  hide?: boolean;
  right: string;
  left: string;
  show: boolean;
  isMobile: boolean | 0 | undefined;
  overlay: boolean;
};
export type FlavourButtonProps = {
  name: string;
  active: boolean;
  show: boolean;
  isMobile: boolean | 0 | undefined;
  overlay: boolean;
};
export type FlavoursProps = {
  id: string;
  name: string;
  yeast: string;
  adv: number | string;
  colour: number | string;
  bitterness: number | string;
  aroma: string;
  image: string;
};

const transition: Transition = {
  ease: transitions.timingFunctions["in-out"],
  damping: 35,
  stiffness: 100,
  duration: 1,
  delay: 0.2,
};

const imageAnimation = (): Variants => ({
  start: {
    opacity: 0,
    transition,
  },
  end: {
    opacity: 1,
    transition,
  },
});
const slideAnimation = (width: string): Variants => ({
  start: {
    width: 0,
    transition,
  },
  end: {
    width: width,
    transition,
  },
});
const fadeAnimation = (): Variants => ({
  start: {
    backgroundColor: "#D9D9D6",
    color: "#2D2F33",
    transition,
  },
  end: {
    backgroundColor: "#284A41",
    color: "#ffffff",
    transition,
  },
});

const FlavourButton: React.FC<FlavourButtonProps> = ({
  name,
  active,
  show,
  overlay,
  isMobile,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  return (
    <motion.div
      ref={ref}
      style={{
        width: "full",
        padding: 1,
        display: "flex",
        justifyContent: "center",
      }}
      variants={fadeAnimation()}
      animate={
        inView && active && show && ((isMobile && overlay) || !isMobile)
          ? "end"
          : "start"
      }
    >
      {name.split(",").length > 1 ? (
        name.split(",").map((land, index) => {
          return (
            <Heading
              key={generateCbKey()}
              as="p"
              sx={{
                width: "full",
                textAlign: "center",
                margin: 0,
                padding: 1,
                fontSize: ["smText", "smText", "paragraphSmall"],
                borderRight:
                  index === name.split(",").length - 1 ? "0" : "1px solid",
              }}
            >
              {land}
            </Heading>
          );
        })
      ) : (
        <Heading
          as="p"
          sx={{
            width: "full",
            textAlign: "center",
            margin: 0,
            padding: 1,
            fontSize: ["smText", "smText", "paragraphSmall"],
          }}
        >
          {name}
        </Heading>
      )}
    </motion.div>
  );
};
const FlavourSlider: React.FC<FlavourSliderProps> = ({
  adv,
  advText,
  hide,
  right,
  left,
  show,
  overlay,
  isMobile,
}) => {
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  return (
    <Box
      sx={{
        width: "full",
        padding: 1,
        paddingInline: 2,
        backgroundColor: "gray",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          position: "relative",
          zIndex: 1,
          justifyContent: "space-between",
        }}
      >
        <Heading
          as="p"
          sx={{
            width: "max-content",
            margin: 0,
            fontSize: ["smText", "smText", "paragraphSmall"],
            color: "white",
          }}
        >
          {right}
        </Heading>
        <Heading
          as="p"
          sx={{
            width: "max-content",
            margin: 0,
            fontSize: ["smText", "smText", "paragraphSmall"],
            color: adv > 98 && show ? "white" : "darkGray",
            transition: "color 1s ease-in-out",
          }}
        >
          {left}
        </Heading>
      </Box>
      <motion.div
        ref={ref}
        style={{
          zIndex: 0,
          textAlign: "end",
          position: "absolute",
          color: "white",
          top: 0,
          bottom: 0,
          left: 0,
          overflow: "hidden",
        }}
        variants={slideAnimation(`${advText && adv < 20 ? 20 : adv}%`)}
        initial={"end"}
        animate={
          inView && show && ((isMobile && overlay) || !isMobile)
            ? "end"
            : "start"
        }
      >
        <Heading
          as="p"
          sx={{
            margin: 0,
            padding: 1,
            paddingInline: 2,
            backgroundColor: "darkGreen",
            height: "100%",
            fontSize: "paragraphSmall",
            color: "white",
            lineHeight: [1.2, "20px", "20px"],
          }}
        >
          {!hide && show ? advText + "%" : ""}
        </Heading>
      </motion.div>
    </Box>
  );
};

const FlavourModule: React.FC<FlavourModuleProps> = ({ themes, bottleIds }) => {
  const [currentFlavour, setCurrent] = useState(0);
  const [flavours, setFlavours] = useState<Flavour[]>([]);
  const [show, setShow] = useState(true);
  const [direction, setDirection] = useState<string | number>("next");
  const [overlay, setOverlay] = useState(false);
  const windowSize = useWindowSize();
  const isMobile = windowSize.width && windowSize.width < 576;
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const theme = themes.includes(THEMES_MAP.layout_bg.layout_bg_white)
    ? "light"
    : themes.includes(THEMES_MAP.layout_bg.layout_bg_lightergray)
    ? "light"
    : "dark";
  const { get } = useDictionaryContext();
  const { currentCulture, setHocFlavourIds } = useSiteContext();

  useEffect(() => {
    if (bottleIds && bottleIds.length > 0) {
      bottleIds.map((bottleId: string) => {
        try {
          fetch(`https://cb-digital-memories.web.app/b/${bottleId}`)
            .then((response) => response.json())
            .then((data) => {
              const bottle: Flavour = data;
              setFlavours((prev) => [...prev, bottle]);
            });
        } catch (err) {
          console.log("err", err);
        }
      });
    }
  }, [bottleIds]);

  useEffect(() => {
    setHocFlavourIds(bottleIds);
  }, [flavours]);

  const baseURL =
    "https://res.cloudinary.com/bottlecollection/image/upload/w_250,c_scale,a_ignore/beers_hoc/";

  if (!flavours || flavours.length === 0) {
    return null;
  }
  return (
    <LayoutContainer
      themes={themes}
      sx={{ position: "relative", height: show ? "auto" : "100%" }}
    >
      <Container>
        <Heading as={"h2"} sx={headingStylesCollection}>
          {get(
            DICTIONARY_MAP.digital_memories.digital_memories_flavour_heading,
            currentCulture
          ) ?? null}
        </Heading>
        <ReactText as={"h3"} sx={textStylesCollection}>
          {get(
            DICTIONARY_MAP.digital_memories.digital_memories_flavour_text,
            currentCulture
          ) ?? null}
        </ReactText>
      </Container>
      <Container sx={{ padding: 0 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: ["1fr", "3fr 3fr", "2fr 3fr"],
            gridGap: 4,
            paddingInline: [5, 5, 15],
            margin: overlay && [-5, -5, 0],
          }}
        >
          <motion.div
            ref={ref}
            variants={imageAnimation()}
            initial={"end"}
            animate={inView && show ? "end" : "start"}
            onAnimationComplete={() => {
              if (!show) {
                if (direction === "next") {
                  if (currentFlavour === flavours.length - 1) {
                    setCurrent(0);
                  } else {
                    setCurrent(currentFlavour + 1);
                  }
                } else if (direction === "prev") {
                  if (currentFlavour === 0) {
                    setCurrent(flavours.length - 1);
                  } else {
                    setCurrent(currentFlavour - 1);
                  }
                } else if (typeof direction === "number") {
                  setCurrent(direction);
                }
              }
            }}
            style={{
              height: isMobile ? "355px" : "auto",
              maxHeight: "80svh",
              display: isMobile && overlay ? "none" : "block",
            }}
          >
            <Image
              onLoad={() => setShow(true)}
              onError={() => setShow(true)}
              alt="cover image"
              src={baseURL + flavours[currentFlavour].filename}
              sx={{
                objectFit: "contain",
                height: "full",
                display: "block",
                margin: "auto",
                maxHeight: "821px",
              }}
            />
          </motion.div>
          {isMobile && !overlay && (
            <>
              <Box
                sx={{
                  ...swiperStyles,
                  ...swiperPaginationStyles,
                  margin: "auto",
                  minHeight: ["21px"],
                }}
              >
                {flavours.map((flavour, index) => {
                  return (
                    <span
                      role="button"
                      style={{ cursor: "pointer" }}
                      key={`${flavour.id}_bullet`}
                      className={`swiper-pagination-bullet ${
                        currentFlavour === index
                          ? "swiper-pagination-bullet-active"
                          : ""
                      }`}
                      onClick={() => {
                        setShow(false);
                        setDirection(index);
                      }}
                    />
                  );
                })}
              </Box>
              <ReactText as={"h3"} sx={textStylesMatch}>
                {get(
                  DICTIONARY_MAP.digital_memories
                    .digital_memories_flavour_yourbestmatch,
                  currentCulture
                ) ?? null}
                {` #${currentFlavour + 1}`}
              </ReactText>
              <Heading as={"h4"} sx={flavourHeadingStyles}>
                {flavours[currentFlavour].title}
              </Heading>
            </>
          )}
          <Box
            className="flavour-details"
            sx={{
              display: isMobile && !overlay ? "none" : "flex",
              flexDirection: "column",
              justifyContent: "start",
              paddingInline: [5, 5, 0],
              left: 0,
              backgroundColor: isMobile ? "lighterGray" : "transparent",
              height: "540px",
            }}
          >
            {!isMobile && (
              <>
                <ReactText as={"h3"} sx={{ ...textStylesMatch, mb: 4 }}>
                  {get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_yourbestmatch,
                    currentCulture
                  ) ?? null}
                  {` #${currentFlavour + 1}`}
                </ReactText>
                <Heading as={"h4"} sx={flavourHeadingStyles}>
                  {flavours[currentFlavour].title}
                </Heading>
                <Box
                  as="hr"
                  sx={{
                    borderTop: "2px solid",
                    borderColor: "gray",
                    width: "full",
                  }}
                />
              </>
            )}
            <Box
              sx={{
                textTransform: "uppercase",
                mb: [3, 5],
              }}
            >
              <Heading
                as="h3"
                sx={{
                  fontSize: ["paragraphMedium", "paragraphMedium", "paragraph"],
                  marginTop: [3, 5],
                  marginBottom: [2, null],
                }}
              >
                {get(
                  DICTIONARY_MAP.digital_memories
                    .digital_memories_flavour_yeasttype,
                  currentCulture
                ) ?? null}
              </Heading>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
                  gridGap: 4,
                }}
              >
                <FlavourButton
                  show={show}
                  isMobile={isMobile}
                  overlay={overlay}
                  name="Ale"
                  active={
                    flavours[currentFlavour].yeast_type.toLowerCase() === "ale"
                  }
                />
                <FlavourButton
                  show={show}
                  isMobile={isMobile}
                  overlay={overlay}
                  name="Lager"
                  active={
                    flavours[currentFlavour].yeast_type.toLowerCase() ===
                    "lager"
                  }
                />
              </Box>
              {isMobile && (
                <>
                  <Heading
                    as="h3"
                    sx={{
                      fontSize: [
                        "paragraphMedium",
                        "paragraphMedium",
                        "paragraph",
                      ],
                      marginTop: 5,
                      marginBottom: [2, null],
                    }}
                  >
                    {get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_flavour_hoparoma,
                      currentCulture
                    ) ?? null}
                  </Heading>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
                      gridGap: 4,
                    }}
                  >
                    <FlavourButton
                      show={show}
                      isMobile={isMobile}
                      overlay={overlay}
                      name="Europe"
                      active={flavours[currentFlavour].hop_aroma
                        .toLowerCase()
                        .includes("euro")}
                    />
                    <FlavourButton
                      show={show}
                      isMobile={isMobile}
                      overlay={overlay}
                      name="USA,AUS,NZL"
                      active={
                        !flavours[currentFlavour].hop_aroma
                          .toLowerCase()
                          .includes("euro")
                      }
                    />
                  </Box>
                </>
              )}
              <Heading
                as="h3"
                sx={{
                  fontSize: ["paragraphMedium", "paragraphMedium", "paragraph"],
                  marginTop: 5,
                  marginBottom: 2,
                }}
              >
                {get(
                  DICTIONARY_MAP.digital_memories
                    .digital_memories_flavour_alcohol,
                  currentCulture
                ) ?? null}
              </Heading>
              <FlavourSlider
                show={show}
                isMobile={isMobile}
                overlay={overlay}
                adv={
                  (parseFloat(flavours[currentFlavour].alcohol_pct) / 10) * 100
                }
                advText={flavours[currentFlavour].alcohol_pct}
                right={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_alcohol_low,
                    currentCulture
                  ) ?? "Low"
                }
                left={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_alcohol_high,
                    currentCulture
                  ) ?? "High"
                }
              />
              <Heading
                as="h3"
                sx={{
                  fontSize: ["paragraphMedium", "paragraphMedium", "paragraph"],
                  marginTop: 5,
                  marginBottom: [2, null],
                }}
              >
                {get(
                  DICTIONARY_MAP.digital_memories
                    .digital_memories_flavour_color,
                  currentCulture
                ) ?? null}
              </Heading>
              <FlavourSlider
                show={show}
                isMobile={isMobile}
                overlay={overlay}
                adv={parseFloat(flavours[currentFlavour].beer_colour_pct)}
                advText={flavours[currentFlavour].beer_colour_pct}
                hide
                right={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_color_light,
                    currentCulture
                  ) ?? "Light"
                }
                left={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_color_dark,
                    currentCulture
                  ) ?? "Dark"
                }
              />
              <Heading
                as="h3"
                sx={{
                  fontSize: ["paragraphMedium", "paragraphMedium", "paragraph"],
                  marginTop: 5,
                  marginBottom: [2, null],
                }}
              >
                {get(
                  DICTIONARY_MAP.digital_memories
                    .digital_memories_flavour_bitterness,
                  currentCulture
                ) ?? null}
              </Heading>
              <FlavourSlider
                show={show}
                isMobile={isMobile}
                overlay={overlay}
                adv={parseFloat(flavours[currentFlavour].bitterness_pct)}
                advText={flavours[currentFlavour].bitterness}
                hide
                right={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_alcohol_low,
                    currentCulture
                  ) ?? "Low"
                }
                left={
                  get(
                    DICTIONARY_MAP.digital_memories
                      .digital_memories_flavour_alcohol_high,
                    currentCulture
                  ) ?? "High"
                }
              />
              {!isMobile && (
                <>
                  <Heading
                    as="h3"
                    sx={{
                      fontSize: [
                        "paragraphMedium",
                        "paragraphMedium",
                        "paragraph",
                      ],
                      marginTop: 5,
                      marginBottom: [2, null],
                    }}
                  >
                    {get(
                      DICTIONARY_MAP.digital_memories
                        .digital_memories_flavour_hoparoma,
                      currentCulture
                    ) ?? null}
                  </Heading>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
                      gridGap: 4,
                    }}
                  >
                    <FlavourButton
                      show={show}
                      isMobile={isMobile}
                      overlay={overlay}
                      name="Europe"
                      active={flavours[currentFlavour].hop_aroma
                        .toLowerCase()
                        .includes("euro")}
                    />
                    <FlavourButton
                      show={show}
                      isMobile={isMobile}
                      overlay={overlay}
                      name="USA,AUS,NZL"
                      active={
                        !flavours[currentFlavour].hop_aroma
                          .toLowerCase()
                          .includes("euro")
                      }
                    />
                  </Box>
                </>
              )}
            </Box>
            {!isMobile && flavours.length > 1 && (
              <Box
                sx={{
                  ...swiperStyles,
                  color: theme === "light" ? "darkGreen" : "white",
                  "svg circle": {
                    fill: theme === "light" ? "darkGreen" : "lightGray",
                  },
                  "svg path": {
                    fill: theme === "light" ? "white" : "darkRed",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 6,
                    pb: [4, 10],
                  }}
                >
                  <button
                    className={`swiper-button-prev`}
                    onClick={() => {
                      setShow(false);
                      setDirection("prev");
                    }}
                    style={{ position: "relative", left: 0, transform: "none" }}
                  >
                    <Box sx={leftArrowSpanStyles}>
                      <Icon sx={leftArrowStyles} svg={MediaSliderArrow} />
                    </Box>
                    <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
                  </button>
                  <Box sx={swiperPaginationStyles}>
                    <Box className="swiper-pagination">
                      {flavours.map((flavour, index) => {
                        return (
                          <span
                            role="button"
                            style={{ cursor: "pointer" }}
                            key={`${flavour.id}_bullet2`}
                            className={`swiper-pagination-bullet ${
                              currentFlavour === index
                                ? "swiper-pagination-bullet-active"
                                : ""
                            }`}
                            onClick={() => {
                              setShow(false);
                              setDirection(index);
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                  <button
                    className={`swiper-button-next`}
                    onClick={() => {
                      setShow(false);
                      setDirection("next");
                    }}
                    style={{
                      position: "relative",
                      right: 0,
                      transform: "none",
                    }}
                  >
                    <Box sx={rightArrowSpanStyles}>
                      <Icon sx={rightArrowStyles} svg={MediaSliderArrow} />
                    </Box>
                    <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
                  </button>
                </Box>
              </Box>
            )}
          </Box>
          {isMobile && (
            <Box
              sx={{
                button: { width: "100%" },
                ...(overlay && {
                  padding: [5, 5, 0],
                }),
              }}
            >
              <Button
                variant="green"
                outline
                onClick={() => {
                  setOverlay((prev) => !prev);
                }}
              >
                {!overlay ? "find out more" : "close"}
              </Button>
            </Box>
          )}
        </Box>
      </Container>
      {isMobile && flavours.length > 1 && !overlay && (
        <Box
          sx={{
            ...swiperStyles,
            color: theme === "light" ? "darkGreen" : "white",
            "svg circle": {
              fill: theme === "light" ? "darkGreen" : "lightGray",
            },
            "svg path": {
              fill: theme === "light" ? "white" : "darkRed",
            },
          }}
        >
          <Fragment>
            <button
              className={`swiper-button-prev`}
              onClick={() => {
                setShow(false);
                setDirection("prev");
              }}
            >
              <Box sx={leftArrowSpanStyles}>
                <Icon sx={leftArrowStyles} svg={MediaSliderArrow} />
              </Box>
              <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
            </button>
            <button
              className={`swiper-button-next`}
              onClick={() => {
                setShow(false);
                setDirection("next");
              }}
            >
              <Box sx={rightArrowSpanStyles}>
                <Icon sx={rightArrowStyles} svg={MediaSliderArrow} />
              </Box>
              <Icon sx={ellipseStyles} svg={MediaSliderEllipse} />
            </button>
          </Fragment>
        </Box>
      )}
    </LayoutContainer>
  );
};

export { FlavourModule };
